import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { db } from '../firebase';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../AuthContext';

const BANK_DETAILS = {
  accountName: 'Dean Patterson',
  sortCode: '04-14-50',
  accountNumber: '66822487',
  bank: 'SumUp Payments Limited',
};

export default function PaymentPage() {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const stateInvoice = location.state?.invoice || null;
  const [invoice, setInvoice] = useState(stateInvoice);
  const [loading, setLoading] = useState(!stateInvoice);
  const [error, setError] = useState('');
  const [method, setMethod] = useState(null);
  const [cardLoading, setCardLoading] = useState(false);
  const [copied, setCopied] = useState('');

  const { currentUser, loading: authLoading } = useAuth();

  useEffect(() => {
    if (location.state?.invoice) {
      if (location.state.invoice.paid) { navigate('/invoices'); return; }
      setInvoice(location.state.invoice);
      setLoading(false);
      return;
    }
    if (!currentUser) { setLoading(true); return; }
    setLoading(true);
    async function load() {
      try {
        const snap = await getDoc(doc(db, 'invoices', invoiceId));
        if (!snap.exists()) { setError('Invoice not found.'); setLoading(false); return; }
        const inv = { id: snap.id, ...snap.data() };
        if (inv.paid) { navigate('/invoices'); return; }
        setInvoice(inv);
        setLoading(false);
      } catch { setError('Could not load invoice.'); setLoading(false); }
    }
    load();
  }, [invoiceId, currentUser]);

  async function handleCardPayment() {
    setCardLoading(true);
    try {
      const res = await fetch('https://europe-west2-le-douit-horticulture.cloudfunctions.net/createPaypalOrder', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          invoiceId: invoice.id,
          amount: invoice.amount,
          description: invoice.description,
        }),
      });
      const { approveUrl, error: fnError } = await res.json();
      if (fnError) throw new Error(fnError);
      window.location.href = approveUrl;
    } catch {
      setError('Could not start PayPal payment. Please try bank transfer or contact Dean directly.');
      setCardLoading(false);
    }
  }

  async function handleBankTransfer() {
    await updateDoc(doc(db, 'invoices', invoiceId), {
      bankTransferViewed: true,
      bankTransferViewedAt: serverTimestamp(),
      paymentMethod: 'bank_transfer',
    });
    setMethod('bank');
  }

  function copy(value, key) {
    navigator.clipboard.writeText(value);
    setCopied(key);
    setTimeout(() => setCopied(''), 2500);
  }

  const reference = invoice ? (invoice.reference || `LDH-${invoiceId.slice(-6).toUpperCase()}`) : '';

  if (!invoice && (authLoading || loading)) return <div className="spinner" style={{ marginTop: 80 }} />;
  if (error) return (
    <div className="page-wrap" style={{ paddingTop: 48 }}>
      <div className="error-msg">{error}</div>
      <button className="btn-secondary" onClick={() => navigate('/invoices')} style={{ marginTop: 12 }}>Back to invoices</button>
    </div>
  );

  return (
    <>
      <div className="app-header">
        <button onClick={() => method ? setMethod(null) : navigate(-1)}
          style={{ background: 'none', color: 'white', fontSize: '1.2rem', width: 'auto', padding: '0 12px 0 0' }}>←</button>
        <div><h1>Pay invoice</h1><div className="subtitle">Garden & Groundworks</div></div>
      </div>

      <div className="page-wrap" style={{ paddingTop: 24 }}>

        {/* Invoice summary card */}
        <div className="card" style={{ marginBottom: 20, borderLeft: '4px solid var(--gold)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>Invoice — {reference}</div>
              <div style={{ fontSize: '1rem', color: 'var(--text-secondary)', marginBottom: 2 }}>{invoice.description}</div>
              <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                {invoice.createdAt?.toDate?.()?.toLocaleDateString('en-GB')}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '1.8rem', fontWeight: 800, color: 'var(--gold)' }}>
                £{invoice.amount?.toFixed(2)}
              </div>
              <span className="badge badge-invoiced">Outstanding</span>
            </div>
          </div>
          {invoice.breakdown?.length > 0 && (
            <>
              <hr className="divider" />
              {invoice.breakdown.map((line, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between',
                  fontSize: '0.875rem', marginBottom: 6 }}>
                  <span style={{ color: 'var(--text-secondary)' }}>{line.label}</span>
                  <span style={{ fontWeight: 600 }}>£{line.amount?.toFixed(2)}</span>
                </div>
              ))}
            </>
          )}
        </div>

        {/* Method selection */}
        {!method && (
          <>
            <p style={{ color: 'var(--text-secondary)', marginBottom: 16, fontSize: '0.95rem', fontWeight: 500 }}>
              How would you like to pay?
            </p>

            {/* Card */}
            <div className="card" style={{ marginBottom: 12, cursor: cardLoading ? 'wait' : 'pointer',
              border: '1.5px solid var(--border)', opacity: cardLoading ? 0.7 : 1 }}
              onClick={!cardLoading ? handleCardPayment : undefined}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{ width: 52, height: 52, background: 'var(--green-pale)', borderRadius: 14,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26, flexShrink: 0 }}>💳</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, marginBottom: 3 }}>Pay by PayPal</div>
                  <div style={{ fontSize: '0.95rem', color: 'var(--text-secondary)' }}>
                    Pay securely with PayPal
                  </div>
                  <div style={{ fontSize: '0.9rem', color: 'var(--green-dark)', marginTop: 3, fontWeight: 600 }}>
                    ✓ Instant confirmation
                  </div>
                </div>
                <div style={{ color: 'var(--text-muted)', fontSize: '1.3rem', fontWeight: 300 }}>
                  {cardLoading ? '⏳' : '›'}
                </div>
              </div>
            </div>

            {/* Bank transfer */}
            <div className="card" style={{ marginBottom: 24, cursor: 'pointer', border: '1.5px solid var(--border)' }}
              onClick={handleBankTransfer}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{ width: 52, height: 52, background: 'var(--gold-pale)', borderRadius: 14,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26, flexShrink: 0 }}>🏦</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, marginBottom: 3 }}>Pay by bank transfer</div>
                  <div style={{ fontSize: '0.95rem', color: 'var(--text-secondary)' }}>
                    Transfer from your banking app — no fees
                  </div>
                  <div style={{ fontSize: '0.9rem', color: 'var(--text-muted)', marginTop: 3 }}>
                    Please pay within 7 days of invoice date
                  </div>
                </div>
                <div style={{ color: 'var(--text-muted)', fontSize: '1.3rem', fontWeight: 300 }}>›</div>
              </div>
            </div>

            <p style={{ textAlign: 'center', fontSize: '0.9rem', color: 'var(--text-muted)' }}>
              🔒 Payments secured by PayPal. Garden & Groundworks never sees your card details.
            </p>
          </>
        )}

        {/* Bank transfer details */}
        {method === 'bank' && (
          <>
            <div className="success-msg" style={{ marginBottom: 16 }}>
              Please transfer <strong>£{invoice.amount?.toFixed(2)}</strong> to the account below.
              Include the payment reference so your payment is matched automatically.
            </div>

            <div className="card" style={{ marginBottom: 16 }}>
              <h3 style={{ fontSize: '1rem', color: 'var(--text-secondary)', marginBottom: 16 }}>
                Bank details
              </h3>

              {[
                { label: 'Account name', value: BANK_DETAILS.accountName, key: 'name' },
                { label: 'Sort code', value: BANK_DETAILS.sortCode, key: 'sort' },
                { label: 'Account number', value: BANK_DETAILS.accountNumber, key: 'acc' },
                { label: 'Bank', value: BANK_DETAILS.bank, key: 'bank' },
              ].map(({ label, value, key }) => (
                <div key={key} style={{ display: 'flex', justifyContent: 'space-between',
                  alignItems: 'center', padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
                  <div>
                    <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: 2 }}>{label}</div>
                    <div style={{ fontWeight: 600 }}>{value}</div>
                  </div>
                  {key !== 'bank' && (
                    <button onClick={() => copy(value, key)}
                      style={{ background: copied === key ? 'var(--green-pale)' : 'var(--bg)',
                        border: '1px solid var(--border)', borderRadius: 8, padding: '6px 14px',
                        fontSize: '0.8rem', color: copied === key ? 'var(--green-dark)' : 'var(--text-secondary)',
                        width: 'auto', fontWeight: 600, transition: 'all 0.2s' }}>
                      {copied === key ? '✓ Copied' : 'Copy'}
                    </button>
                  )}
                </div>
              ))}

              {/* Reference — highlighted */}
              <div style={{ marginTop: 16, background: 'var(--gold-pale)', borderRadius: 10,
                padding: '14px 16px', border: '2px solid var(--gold)' }}>
                <div style={{ fontSize: '0.9rem', color: '#7D4A00', fontWeight: 600, marginBottom: 6 }}>
                  ⚠️ Payment reference — must include this
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ fontWeight: 800, fontSize: '1.15rem', letterSpacing: '0.06em', color: '#5D3400' }}>
                    {reference}
                  </div>
                  <button onClick={() => copy(reference, 'ref')}
                    style={{ background: copied === 'ref' ? 'var(--green-mid)' : 'var(--gold)',
                      color: 'white', border: 'none', borderRadius: 8, padding: '9px 16px',
                      fontSize: '0.85rem', width: 'auto', fontWeight: 700, transition: 'all 0.2s' }}>
                    {copied === 'ref' ? '✓ Copied!' : 'Copy ref'}
                  </button>
                </div>
              </div>
            </div>

            <div style={{ background: 'var(--green-pale)', borderRadius: 12, padding: '14px 16px',
              border: '1px solid var(--green-light)', marginBottom: 20, fontSize: '1rem',
              color: 'var(--green-dark)', lineHeight: 1.5 }}>
              <strong>What happens next?</strong><br />
              Once your payment arrives Dean will confirm receipt and mark your invoice as paid.
              You'll receive a notification when this is done.
            </div>

            <button className="btn-secondary" onClick={() => setMethod(null)}>
              ← Choose different payment method
            </button>
          </>
        )}
      </div>
    </>
  );
}
